import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject} from 'highcharts';
import {CountryChartData} from '../../../../models/country-chart-data.interface';
import {COMPOSITION_COLORS} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

interface CompositionData {
  category: string;
  countryData: CountryChartData[];
}

@Component({
  selector: 'app-composition-pie-chart',
  templateUrl: './composition-pie-chart.component.html',
  styleUrls: ['./composition-pie-chart.component.scss']
})
export class CompositionPieChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: any[];
  countriesText = '';
  countryCodes!: string[];
  textBelowCodePOM = 'kfcharts.Country.PutOnMarket.Composition.textBelow';
  textBelowCodeCollected = 'kfcharts.Country.Collected.Composition.textBelow';
  isAdmin!: boolean;

  @Input()
  data!: CountryChartData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  countryCategory!: string;
  @Input()
  countries!: string[];

  private static getCountryCodes(chartData: CountryChartData[]): string[] {
    const chartCodes = chartData.map(item => item.countryCode);
    return [...new Set(chartCodes)];
  }

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(
      this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    this.setCountriesText();
    if (this.data) {
      this.countryCodes = CompositionPieChartComponent.getCountryCodes(this.data);
      const normalizedData = this.normalizeData(this.groupByPOMCategory(this.data), this.countryCodes);

      // highcharts
      this.chartData = this.convertToChartData(normalizedData);
      this.chart = new Chart({
        chart: {
          type: 'pie',
          options3d: {
            enabled: true,
            alpha: 45,
            beta: 0
          }
        },
        colors: COMPOSITION_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        caption: {
          text: `<strong>Countries:</strong> ${this.countriesText}`,
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        plotOptions: {
          pie: {
            dataLabels: {
              enabled: true,
              format: '{point.name}'
            }
          }
        },
        accessibility: {
          point: {
            valueSuffix: '%'
          }
        },
        tooltip: {
          formatter(): string[]|undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return [this.point.name];
              }
              return ['<div class="center"><b>' + this.point.name + ' - ' + formatNumber(this.point?.percentage) + '%' + '</b></div><br><br>',
                (pointOptions.custom?.countryCategory === 'putonmarket' ? 'Put on market: ' : 'Collected: ')
                + formatNumber(pointOptions.custom?.absolute) + ' tons' + '<br>'
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        series: [{
          name: 'Tons',
          type: 'pie',
          data: this.chartData
        }]
      });
    }
  }

  getCountryCategory = () => {
    return this.countryCategory;
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {state: { code: this.countryCategory === 'putonmarket' ? this.textBelowCodePOM : this.textBelowCodeCollected }}).then(
      () => {
        // do nothing
      }
    );
  }

  private normalizeData(groupedByYear: CompositionData[], countryCodes: string[]): CompositionData[] {
    const normalizedData: CompositionData[] = [];
    groupedByYear.forEach(item => {
      if (item.countryData.length === countryCodes.length) {
        normalizedData.push(item);
      } else {
        const newCountryData: CountryChartData[] = [];
        countryCodes.forEach(code => {
          let countryDataItem = item.countryData.find(c => c.countryCode === code);
          if (!countryDataItem) {
            countryDataItem = {
              countryCode: code,
              absolute: 0,
              year: Number(item.category),
              population: 0,
              countryName: code
            } as CountryChartData;
          }
          newCountryData.push(countryDataItem);
        });
        normalizedData.push({category: item.category, countryData: newCountryData});
      }
    });
    return normalizedData;
  }

  private convertToChartData(data: CompositionData[]): any[] {
    this.chart?.ref?.hideData();
    const chartData: any[] = [] ;
    data.forEach(item => {
      chartData.push({
        y: (item.countryData[0].absolute),
        name: item.category,
        custom: {absolute: item.countryData[0].absolute, percentage: data, countryCategory: this.countryCategory}
      });
    });
    return chartData;
  }

  private groupByPOMCategory(objectArray: Array<any>): CompositionData[] {
    const yearCountryDataArray: CompositionData[] = [];
    const groupedArray = objectArray.reduce((acc, obj) => {
      const key = obj.countryName;
      if (!acc[key]) {
        acc[key] = [];
      }
      // Add object to list for given key's value
      acc[key].push(obj);
      return acc;
    }, {});

    Object.getOwnPropertyNames(groupedArray).forEach((key: string) => {
      const countryWeeeSystemData = {} as CompositionData;
      countryWeeeSystemData.category = key;
      countryWeeeSystemData.countryData = groupedArray[key];
      yearCountryDataArray.push(countryWeeeSystemData);
    });

    return yearCountryDataArray;
  }

  private setCountriesText(): void {
    this.countriesText = '';
    this.countries?.forEach((country, index) => {
      if (index === 0) {
        this.countriesText = ` ${country}`;
      }
      else {
        this.countriesText += `, ${country}`;
      }
    });
  }

}

